// Bottom-left HUD deck: vitals (segmented HP with shield overlay, dash pips),
// ability dials (signature cooldown sweep, ultimate charge) and the weapon
// panel (two slots, heat gauge with overheat lock, charge bar).
// Every widget takes plain values in set(); redraws happen only on change so
// polling the GameContext every frame stays cheap.

import Phaser from 'phaser';
import { mechKeys } from '../../art/keys';
import { art } from '../../art/registry';
import { COLORS, css, mix } from '../../config/palette';
import type { WeaponId } from '../../core/types';
import { weaponName } from '../format';
import {
  ActionHint,
  HeatGauge,
  Panel,
  SegmentedBar,
  TYPE,
  chamferPoints,
  glowTexture,
  iconTexture,
  iconTint,
  txt,
  type UiAction,
} from '../kit';

/** Translucent backing used by every deck module (keeps the gameplay readable underneath). */
export function deckPanel(scene: Phaser.Scene, x: number, y: number, w: number, h: number, accent: number): Panel {
  return new Panel(scene, x, y, w, h, { fillAlpha: 0.72, border: COLORS.uiBorder, borderAlpha: 0.9, accent, ticks: true, chamfer: 12 });
}

// ───────────────────────────── Vitals ─────────────────────────────

export interface VitalsState {
  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;
  /** Dashes available before landing. */
  dashCount: number;
  /** 0..1 dash recharge (1 = ready). */
  dashReady: number;
}

/** HP + shield bars, numbers, dash pips. Origin = top-left of the panel. */
export class VitalsHud extends Phaser.GameObjects.Container {
  static readonly W = 476;
  static readonly H = 118;
  private readonly panel: Panel;
  private readonly nameText: Phaser.GameObjects.Text;
  private readonly hpText: Phaser.GameObjects.Text;
  private readonly hpMaxText: Phaser.GameObjects.Text;
  private readonly shieldText: Phaser.GameObjects.Text;
  private readonly critText: Phaser.GameObjects.Text;
  private readonly shieldBar: SegmentedBar;
  private readonly hpBar: SegmentedBar;
  private readonly pips: Phaser.GameObjects.Graphics;
  private readonly breakText: Phaser.GameObjects.Text;
  private accent: number;
  private lastHp = -1;
  private lastMax = -1;
  private lastShield = -1;
  private lastPips = '';
  private critT = 0;
  private low = false;

  constructor(scene: Phaser.Scene, x: number, y: number, mechName: string, accent: number) {
    super(scene, x, y);
    this.accent = accent;
    const W = VitalsHud.W;
    this.panel = deckPanel(scene, 0, 0, W, VitalsHud.H, accent);
    this.nameText = txt(scene, 20, 12, mechName.toUpperCase(), { font: 'display', size: TYPE.label + 2, weight: 700, color: accent, spacing: 4 });
    this.hpMaxText = txt(scene, W - 20, 10, '/ 100', { font: 'display', size: TYPE.label, weight: 600, color: COLORS.uiDim, originX: 1 });
    this.hpText = txt(scene, 0, 4, '100', { font: 'display', size: 30, weight: 700, color: 0xffffff, originX: 1 });
    this.shieldText = txt(scene, 0, 10, '', { font: 'display', size: TYPE.label + 1, weight: 700, color: COLORS.shieldBar, originX: 1 });
    this.shieldBar = new SegmentedBar(scene, 20, 50, W - 40, 9, { color: COLORS.shieldBar, segment: 0, ghostColor: 0xffffff, chamfer: 0 });
    this.hpBar = new SegmentedBar(scene, 20, 61, W - 40, 24, { color: COLORS.hp, segment: 10, ghostColor: 0xfff0f0 });
    this.pips = scene.add.graphics();
    const dashLabel = txt(scene, 20, 92, 'DASH', { font: 'display', size: TYPE.micro, weight: 600, color: COLORS.uiDim, spacing: 2 });
    this.critText = txt(scene, W - 20, 92, 'CRITICAL', { font: 'display', size: TYPE.micro, weight: 700, color: COLORS.uiBad, spacing: 3, originX: 1 }).setVisible(false);
    this.breakText = txt(scene, W - 20, 30, 'SHIELD BROKEN', { font: 'display', size: TYPE.micro, weight: 700, color: COLORS.shieldBar, spacing: 2, originX: 1 }).setAlpha(0);
    this.add([this.panel, this.nameText, this.hpText, this.hpMaxText, this.shieldText, this.shieldBar, this.hpBar, dashLabel, this.pips, this.critText, this.breakText]);
    scene.add.existing(this);
  }

  setIdentity(name: string, accent: number): void {
    this.nameText.setText(name.toUpperCase()).setColor(css(accent));
    if (accent !== this.accent) {
      this.accent = accent;
      this.panel.setStyle({ accent });
      this.lastPips = '';
    }
  }

  set(s: VitalsState, instant = false): void {
    const hp = Math.max(0, Math.ceil(s.hp));
    const max = Math.max(1, Math.round(s.maxHp));
    const sh = Math.max(0, Math.ceil(s.shield));
    this.hpBar.set(s.hp, s.maxHp, instant);
    this.shieldBar.set(s.shield, Math.max(1, s.maxShield), instant);
    this.shieldBar.setVisible(s.maxShield > 0);
    if (hp !== this.lastHp || max !== this.lastMax) {
      if (hp > this.lastHp && this.lastHp >= 0 && !instant) this.hpBar.flash(COLORS.heal, 0.4);
      this.lastHp = hp;
      this.lastMax = max;
      this.hpMaxText.setText(`/ ${max}`);
      this.hpText.setText(String(hp)).setX(this.hpMaxText.x - this.hpMaxText.width - 8);
      this.low = hp / max < 0.25;
      this.critText.setVisible(this.low);
      this.hpText.setColor(css(this.low ? COLORS.uiBad : 0xffffff));
      this.layoutShieldText();
    }
    if (sh !== this.lastShield) {
      this.lastShield = sh;
      this.shieldText.setText(sh > 0 ? `+${sh}` : '');
      this.layoutShieldText();
    }
    // Dash pips: one per dash, the recharge fills them left to right.
    const n = Math.max(1, Math.min(5, Math.round(s.dashCount)));
    const ready = Phaser.Math.Clamp(s.dashReady, 0, 1);
    const key = `${n}:${Math.round(ready * 40)}:${this.accent}`;
    if (key !== this.lastPips) {
      this.lastPips = key;
      this.drawPips(n, ready);
    }
  }

  private layoutShieldText(): void {
    this.shieldText.setX(this.hpText.x - this.hpText.width - 12);
  }

  private drawPips(n: number, ready: number): void {
    const g = this.pips;
    g.clear();
    const x0 = 92;
    const y = 96;
    const w = 38;
    const h = 10;
    for (let i = 0; i < n; i++) {
      const x = x0 + i * (w + 8);
      const pts = [new Phaser.Math.Vector2(x + 5, y), new Phaser.Math.Vector2(x + w, y), new Phaser.Math.Vector2(x + w - 5, y + h), new Phaser.Math.Vector2(x, y + h)];
      g.fillStyle(0x070a0f, 0.9);
      g.fillPoints(pts, true);
      const f = ready >= 1 ? 1 : ready;
      if (f > 0) {
        g.fillStyle(ready >= 1 ? this.accent : mix(this.accent, COLORS.steel3, 0.55), 1);
        const fw = w * f;
        g.fillPoints([new Phaser.Math.Vector2(x + 5, y), new Phaser.Math.Vector2(x + Math.max(5, fw), y), new Phaser.Math.Vector2(x + Math.max(5, fw) - 5, y + h), new Phaser.Math.Vector2(x, y + h)], true);
      }
      g.lineStyle(1, ready >= 1 ? mix(this.accent, 0xffffff, 0.3) : COLORS.uiBorder, 1);
      g.strokePoints(pts, true, true);
    }
  }

  /** Shield hit 0: flash the bar and show a short label. */
  shieldBreak(reduced: boolean): void {
    this.shieldBar.flash(0xffffff, reduced ? 0.2 : 0.45);
    this.scene.tweens.killTweensOf(this.breakText);
    this.breakText.setAlpha(1).setY(30);
    this.scene.tweens.add({ targets: this.breakText, alpha: 0, y: 24, delay: 900, duration: 500 });
  }

  /** Damage flash on the HP bar. */
  hurt(): void {
    this.hpBar.flash(0xffffff, 0.18);
  }

  tick(dt: number, reduced: boolean): void {
    this.hpBar.tick(dt);
    this.shieldBar.tick(dt);
    if (this.low) {
      this.critT += dt;
      this.critText.setAlpha(reduced ? 1 : 0.55 + 0.45 * Math.abs(Math.sin(this.critT * 4)));
    }
  }
}

// ───────────────────────────── Ability dials ─────────────────────────────

export interface DialState {
  /** 0..1 progress towards ready. */
  progress: number;
  ready: boolean;
  /** Ability currently running (ultimate active / signature active). */
  active: boolean;
  /** Centre text while recharging (seconds) or below (percent). */
  text: string;
}

/**
 * Circular ability icon. 'cooldown' dials darken with a clockwise radial sweep
 * that uncovers the icon as the cooldown ends; 'charge' dials fill an outer arc
 * and glow when ready.
 */
export class AbilityDial extends Phaser.GameObjects.Container {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly glow: Phaser.GameObjects.Image;
  private readonly pulseRing: Phaser.GameObjects.Graphics;
  private icon: Phaser.GameObjects.Image;
  private readonly centerText: Phaser.GameObjects.Text;
  private readonly subText: Phaser.GameObjects.Text;
  private readonly hint: ActionHint;
  private color: number;
  private dial = { progress: -1, ready: false, active: false, text: '' };
  private t = 0;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    private readonly r: number,
    iconKeyStr: string,
    color: number,
    action: UiAction,
    private readonly mode: 'cooldown' | 'charge',
  ) {
    super(scene, x, y);
    this.color = color;
    this.glow = scene.add.image(0, 0, glowTexture(scene)).setDisplaySize(r * 4, r * 4).setTint(color).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0);
    this.g = scene.add.graphics();
    this.icon = this.makeIcon(iconKeyStr);
    this.centerText = txt(scene, 0, 0, '', { font: 'display', size: 26, weight: 700, color: 0xffffff, originX: 0.5, originY: 0.5, stroke: { color: COLORS.ink, width: 4 } });
    this.pulseRing = scene.add.graphics().setAlpha(0);
    this.hint = new ActionHint(scene, action, '', { size: 24 });
    this.subText = txt(scene, 0, r + 20, '', { font: 'display', size: TYPE.micro, weight: 700, color: COLORS.uiDim, originY: 0.5, spacing: 1 });
    this.add([this.glow, this.g, this.icon, this.centerText, this.pulseRing, this.hint, this.subText]);
    this.layoutHint();
    scene.add.existing(this);
  }

  private makeIcon(key: string): Phaser.GameObjects.Image {
    const k = iconTexture(this.scene, key);
    const img = this.scene.add.image(0, 0, k);
    const fw = img.frame.width || 1;
    const fh = img.frame.height || 1;
    img.setScale(Math.min((this.r * 1.15) / fw, (this.r * 1.15) / fh));
    const tint = iconTint(this.scene, k, 0xffffff);
    if (tint !== 0xffffff) img.setTint(tint);
    return img;
  }

  private layoutHint(): void {
    // Glyph under the dial; the charge dial puts its percent next to it.
    if (this.mode === 'charge') {
      const total = this.hint.width + 8 + this.subText.width;
      this.hint.setPosition(-total / 2, this.r + 20);
      this.subText.setX(-total / 2 + this.hint.width + 8);
    } else {
      this.hint.setPosition(-this.hint.width / 2, this.r + 20);
      this.subText.setVisible(false);
    }
  }

  setColor(color: number): void {
    if (color === this.color) return;
    this.color = color;
    this.glow.setTint(color);
    this.dial.progress = -1;
  }

  setIconKey(key: string): void {
    const img = this.makeIcon(key);
    this.icon.destroy();
    this.icon = img;
    this.addAt(img, 2);
    this.dial.progress = -1;
  }

  set(s: DialState, reduced: boolean): void {
    const p = Phaser.Math.Clamp(s.progress, 0, 1);
    const changed = Math.abs(p - this.dial.progress) > 0.004 || s.ready !== this.dial.ready || s.active !== this.dial.active || s.text !== this.dial.text;
    if (!changed) return;
    const becameReady = s.ready && !this.dial.ready && this.dial.progress >= 0;
    this.dial = { progress: p, ready: s.ready, active: s.active, text: s.text };
    this.redraw();
    if (this.mode === 'cooldown') {
      this.centerText.setText(s.ready || s.active ? '' : s.text);
    } else {
      this.subText.setText(s.active ? 'ACTIVE' : s.ready ? 'READY' : s.text);
      this.subText.setColor(css(s.ready || s.active ? this.color : COLORS.uiDim));
      this.layoutHint();
    }
    if (becameReady) this.readyPulse(reduced);
  }

  private redraw(): void {
    const { progress: p, ready, active } = this.dial;
    const g = this.g;
    const r = this.r;
    const c = this.color;
    const top = -Math.PI / 2;
    g.clear();
    g.fillStyle(0x070a0f, 0.92);
    g.fillCircle(0, 0, r);
    g.fillStyle(c, ready || active ? 0.2 : 0.06);
    g.fillCircle(0, 0, r - 4);
    // Track + progress arc.
    g.lineStyle(5, COLORS.steel1, 1);
    g.strokeCircle(0, 0, r + 1);
    if (p > 0) {
      g.lineStyle(5, ready || active ? mix(c, 0xffffff, 0.25) : mix(c, COLORS.steel3, 0.35), 1);
      g.beginPath();
      g.arc(0, 0, r + 1, top, top + Math.PI * 2 * p, false);
      g.strokePath();
    }
    if (this.mode === 'cooldown' && !ready && !active) {
      // Radial sweep: the still-cooling share of the icon stays dark.
      g.fillStyle(0x000000, 0.62);
      g.slice(0, 0, r - 3, top + Math.PI * 2 * p, top + Math.PI * 2, false);
      g.fillPath();
      g.lineStyle(2, c, 0.9);
      const a = top + Math.PI * 2 * p;
      g.lineBetween(0, 0, Math.cos(a) * (r - 3), Math.sin(a) * (r - 3));
    }
    g.lineStyle(1.5, ready || active ? c : COLORS.uiBorder, 1);
    g.strokeCircle(0, 0, r - 4);
    this.icon.setAlpha(ready || active ? 1 : this.mode === 'charge' ? 0.45 + 0.4 * p : 0.55);
    this.icon.setTint(ready || active ? 0xffffff : 0xb8c2d0);
  }

  private readyPulse(reduced: boolean): void {
    const g = this.pulseRing;
    g.clear();
    g.lineStyle(3, this.color, 1);
    g.strokeCircle(0, 0, this.r);
    g.setScale(1).setAlpha(reduced ? 0.5 : 1);
    this.scene.tweens.killTweensOf(g);
    this.scene.tweens.add({ targets: g, scale: 1.7, alpha: 0, duration: 520, ease: 'Cubic.easeOut' });
  }

  tick(dt: number, reduced: boolean): void {
    this.t += dt;
    const lit = this.dial.ready || this.dial.active;
    const target = lit ? (reduced ? 0.32 : 0.26 + 0.18 * Math.sin(this.t * (this.dial.active ? 9 : 3.2))) : 0;
    this.glow.setAlpha(this.glow.alpha + (target - this.glow.alpha) * Math.min(1, dt * 10));
  }
}

// ───────────────────────────── Weapon panel ─────────────────────────────

export interface WeaponSlotState {
  id: WeaponId | null;
  name?: string;
}

export interface WeaponPanelState {
  slots: [WeaponSlotState, WeaponSlotState];
  active: number;
  heat: number;
  overheated: boolean;
  overheatRemaining: number;
  /** 0..1 for charge weapons, < 0 otherwise. */
  charge: number;
}

interface SlotView {
  root: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Graphics;
  num: Phaser.GameObjects.Text;
  icon: Phaser.GameObjects.Image | null;
  name: Phaser.GameObjects.Text;
  id: WeaponId | null | undefined;
}

/** Two weapon slots (active highlighted) + heat gauge of the held weapon. Origin = top-left. */
export class WeaponPanel extends Phaser.GameObjects.Container {
  static readonly W = 352;
  static readonly H = 118;
  private readonly panel: Panel;
  private readonly slots: SlotView[] = [];
  private readonly heat: HeatGauge;
  private readonly heatLabel: Phaser.GameObjects.Text;
  private readonly lockText: Phaser.GameObjects.Text;
  private accent: number;
  private activeIdx = -1;
  private overheated = false;
  private lockT = 0;
  private lastLock = '';

  constructor(scene: Phaser.Scene, x: number, y: number, accent: number) {
    super(scene, x, y);
    this.accent = accent;
    const W = WeaponPanel.W;
    this.panel = deckPanel(scene, 0, 0, W, WeaponPanel.H, accent);
    this.add(this.panel);
    for (let i = 0; i < 2; i++) {
      const root = scene.add.container(0, 10 + i * 38);
      const bg = scene.add.graphics();
      const num = txt(scene, 28, 17, String(i + 1), { font: 'display', size: TYPE.micro, weight: 700, color: COLORS.uiDim, originX: 0.5, originY: 0.5 });
      const name = txt(scene, 156, 17, '', { font: 'display', size: TYPE.label, weight: 600, color: COLORS.uiText, originY: 0.5, spacing: 1 });
      root.add([bg, num, name]);
      this.add(root);
      this.slots.push({ root, bg, num, icon: null, name, id: undefined });
    }
    this.heatLabel = txt(scene, 16, 96, 'HEAT', { font: 'display', size: TYPE.micro - 2, weight: 700, color: COLORS.uiDim, spacing: 2, originY: 0.5 });
    this.heat = new HeatGauge(scene, 66, 90, W - 84, 12);
    this.lockText = txt(scene, 66 + (W - 84) / 2, 96, '', { font: 'display', size: TYPE.micro - 1, weight: 700, color: 0xffffff, spacing: 3, originX: 0.5, originY: 0.5, stroke: { color: COLORS.ink, width: 4 } });
    this.add([this.heatLabel, this.heat, this.lockText]);
    scene.add.existing(this);
  }

  setAccent(accent: number): void {
    if (accent === this.accent) return;
    this.accent = accent;
    this.panel.setStyle({ accent });
    this.activeIdx = -1;
  }

  set(s: WeaponPanelState): void {
    s.slots.forEach((slot, i) => {
      const v = this.slots[i];
      if (slot.id !== v.id) this.rebuildSlot(i, slot);
    });
    if (s.active !== this.activeIdx) {
      this.activeIdx = s.active;
      this.slots.forEach((_, i) => this.drawSlot(i));
    }
    this.heat.set(s.heat, s.overheated, s.charge);
    this.overheated = s.overheated;
    const lock = s.overheated ? `OVERHEAT  ${Math.max(0, s.overheatRemaining).toFixed(1)}s` : '';
    if (lock !== this.lastLock) {
      this.lastLock = lock;
      this.lockText.setText(lock);
      this.heatLabel.setColor(css(s.overheated ? COLORS.threat : s.heat > 0.75 ? COLORS.heatBar : COLORS.uiDim));
    }
  }

  private rebuildSlot(i: number, slot: WeaponSlotState): void {
    const v = this.slots[i];
    v.id = slot.id;
    v.icon?.destroy();
    v.icon = null;
    if (slot.id) {
      const key = art.weapons[slot.id]?.key ?? mechKeys.weapon(slot.id);
      const k = iconTexture(this.scene, key);
      const img = this.scene.add.image(90, 17, k);
      const fw = img.frame.width || 1;
      const fh = img.frame.height || 1;
      img.setScale(Math.min(96 / fw, 30 / fh, 2));
      v.icon = img;
      v.root.addAt(img, 1);
    }
    v.name.setText((slot.name ?? (slot.id ? weaponName(slot.id) : 'EMPTY')).toUpperCase());
    this.drawSlot(i);
  }

  private drawSlot(i: number): void {
    const v = this.slots[i];
    const on = i === this.activeIdx;
    const g = v.bg;
    const w = WeaponPanel.W - 24;
    g.clear();
    if (on) {
      g.fillStyle(mix(COLORS.uiPanel, this.accent, 0.22), 0.95);
      g.fillPoints(chamferPoints(12, 0, w, 34, { tl: 8, br: 8 }), true);
      g.lineStyle(1.5, this.accent, 0.9);
      g.strokePoints(chamferPoints(12, 0, w, 34, { tl: 8, br: 8 }), true, true);
      g.fillStyle(this.accent, 1);
      g.fillRect(12, 6, 3, 22);
    }
    const empty = !v.id;
    v.num.setColor(css(on ? this.accent : COLORS.uiDim));
    v.name.setColor(css(empty ? COLORS.steel3 : on ? 0xffffff : COLORS.uiDim));
    v.name.setFontSize(on ? TYPE.label : TYPE.micro);
    v.root.setAlpha(empty ? 0.6 : on ? 1 : 0.72);
    if (v.icon) {
      v.icon.setAlpha(on ? 1 : 0.55);
      // Fallback pictograms are white: tint them; real weapon art keeps its colours.
      const tint = iconTint(this.scene, v.icon.texture.key, on ? mix(this.accent, 0xffffff, 0.4) : COLORS.uiDim);
      if (tint !== 0xffffff) v.icon.setTint(tint);
      else v.icon.clearTint();
    }
  }

  tick(dt: number, reduced: boolean): void {
    this.heat.tick(dt, reduced);
    if (this.overheated) {
      this.lockT += dt;
      this.lockText.setAlpha(reduced ? 1 : 0.6 + 0.4 * Math.abs(Math.sin(this.lockT * 6)));
    }
  }
}
